/**
 * Pure set-math helpers for the Teams (Admin) views — the duplicate-agent
 * rules (PET-665 / PET-666) and the per-Community eligible-agent pool. Split
 * out of AccessControl.tsx so the module can export non-component values
 * without tripping react-refresh's only-export-components rule, and so the
 * derivations can be unit-tested in isolation.
 */
import type { AdminScopedCommunity, DialpadUserRow, TeamOut } from "../lib/api";

// Agent ids that appear in more than one team. Membership is de-duped
// within each team first (an agent listed twice on the same team is not a
// cross-team duplicate). Exported so the derivation can be unit-tested.
export function computeDuplicateAgentIds(teams: readonly TeamOut[]): Set<string> {
  const counts = new Map<string, number>();
  for (const t of teams) {
    for (const id of new Set(t.dialpad_user_ids)) {
      counts.set(id, (counts.get(id) ?? 0) + 1);
    }
  }
  const dups = new Set<string>();
  for (const [id, c] of counts) if (c > 1) dups.add(id);
  return dups;
}

// Agent ids that belong to any team other than `editingId` (the team open
// in the editor, or null for "new"). The team being edited is skipped so
// its own members are not excluded from its own dropdown. Exported so the
// derivation can be unit-tested.
export function computeAgentsInOtherTeams(
  teams: readonly TeamOut[],
  editingId: string | null,
): Set<string> {
  const set = new Set<string>();
  for (const t of teams) {
    if (t.id === editingId) continue;
    for (const id of t.dialpad_user_ids) set.add(id);
  }
  return set;
}

// The agent pool eligible for a Community's Teams. Mirrors the backend
// membership rule in services/group_resolution.py (`_COMMUNITY_INSERT_SQL`):
// an agent qualifies when they are NOT force-excluded AND either their brand
// is one of the Community's brands OR they are force-included (an include
// override wins regardless of brand). Exported so the Team agent picker can
// scope its add-options to this pool — offering the full Dialpad directory
// leaks agents from other Communities into the picker. Accepts any object
// carrying `brands` + `exceptions` (AdminScopedCommunity or CommunityOut).
export function computeEligibleAgents(
  community: Pick<AdminScopedCommunity, "brands" | "exceptions"> | null | undefined,
  dialpadUsers: readonly DialpadUserRow[],
): DialpadUserRow[] {
  const brandSet = new Set(community?.brands ?? []);
  const includeSet = new Set(
    (community?.exceptions ?? [])
      .filter((e) => e.mode === "include")
      .map((e) => e.dialpad_user_id),
  );
  const excludeSet = new Set(
    (community?.exceptions ?? [])
      .filter((e) => e.mode === "exclude")
      .map((e) => e.dialpad_user_id),
  );
  return dialpadUsers.filter((u) => {
    if (excludeSet.has(u.id)) return false;
    if (includeSet.has(u.id)) return true;
    return !!u.brand && brandSet.has(u.brand);
  });
}
